/**
 * @fileoverview Pro Forma account addition page
 */

import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import { addPhase } from "../api/phaseApi";
import { addAdvancedControl } from "../api/advancedcontrolsapi";
import { createControls } from "../api/controlsapi";
import { createUserMetadata } from "../api/usermetadataapi";
import useAuthStore from "../store/useAuthStore";
import { uploadWithOutDelete } from "../api/uploadapi";
import validatePassword from "../utils/validatePassword";
import { parseCSV } from "../utils/parseCsv";
import { remap_chargerCSV_items } from "../utils/remapItems";
import { chargerDataFormatted } from "../utils/chargerDataFormatted";
import { phasesDataList } from "../api/defaultapi";
import fetchData from "../utils/fetchData";
import { addChargerCost } from "../api/chargercostapi";
import AddressConfirmationDialog from "../components/create-account/AddressConfirmationDialog";
import { prepareDomicileAddressGroups } from "../utils/siteAddress";
import { checkUserExists } from "../api/adminapi";

const Admin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [chargerFile, setChargerFile] = useState(null);
  const [message, setMessage] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [domicileGroups, setDomicileGroups] = useState([]);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const navigate = useNavigate();
  const { signUp, fetchSignupData } = useAuthStore();
  const MAX_LENGTH = 19;

  const { getRootProps: getCSVRootProps, getInputProps: getCSVInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setCsvFile(acceptedFiles[0]);
      setAddressDialogOpen(false);
      setDomicileGroups([]);
      setMessage("");
    },
    accept: ".csv",
  });

  const { getRootProps: getChargerRootProps, getInputProps: getChargerInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setChargerFile(acceptedFiles[0]);
    },
    accept: ".csv",
  });

  /**
   * Confirm the username is available, parse Vehicle CSV, then open domicile confirmation.
   * Account creation runs only after Geocodio verifies the addresses.
   */
  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage("");
    setNotificationMessage("");

    if (!csvFile) {
      setMessage("Please upload a Vehicle CSV file");
      return;
    }

    const trimmedUsername = email.trim();
    if (!trimmedUsername) {
      setMessage("Please enter a username");
      return;
    }

    if (!validatePassword(password)) {
      setMessage(
        "Password must be at least 8 characters long, include at least one capital letter (A-Z), include at least one number, and at least one special character.",
      );
      return;
    }

    setIsCheckingUser(true);
    try {
      const userCheck = await checkUserExists(trimmedUsername);
      if (userCheck.error) {
        setMessage(`Unable to verify username: ${userCheck.error}`);
        return;
      }
      if (userCheck.exists) {
        setMessage("This username is already registered");
        return;
      }

      const groups = await prepareDomicileAddressGroups(csvFile);
      setDomicileGroups(groups);
      setAddressDialogOpen(true);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setIsCheckingUser(false);
    }
  };

  const createAccountWithAddresses = async (domicileAddressMap) => {
    setIsCreatingAccount(true);
    setMessage("");

    const newEmail = email + "@optonyusa.com";
    const { data, error } = await signUp(newEmail, password);
    const errorMessage = [];

    if (error) {
      setMessage(`${error.message}`);
      setIsCreatingAccount(false);
      return;
    }

    try {
      const csvResponse = await uploadCSVFile(data.user.id, domicileAddressMap);
      const chargerCSVResponse = await uploadChargerFile(data.user.id);
      fetchSignupData(data.user.id);

      if (csvResponse) {
        errorMessage.push(`${csvResponse.error}\n`);
      }

      if (chargerCSVResponse && chargerCSVResponse.error) {
        errorMessage.push(`${chargerCSVResponse.error}`);
      }

      setAddressDialogOpen(false);

      if (errorMessage.length > 0) {
        let countdown = 10;
        const countdownInterval = setInterval(() => {
          if (countdown > 0) {
            setNotificationMessage(
              `\n Account has been created successfully. You will proceed to the dashboard in ${countdown} seconds`,
            );
          } else {
            clearInterval(countdownInterval);
            navigate("/");
          }
          countdown--;
        }, 1000);
      } else {
        setNotificationMessage("");
        navigate("/");
      }
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const uploadChargerFile = async (userId) => {
    // If no charger file is provided, return early without error
    if (!chargerFile) {
      return null;
    }

    const formData = new FormData();
    formData.append("file", chargerFile);
    formData.append("user_id", userId);

    try {
      const file = formData.get("file");
      const userId = formData.get("user_id");

      let { results } = await parseCSV(file);
      results = remap_chargerCSV_items(results);
      results.forEach(async (result) => {
        result.user_id = userId;
      });

      results = chargerDataFormatted(results);

      // check if required fields are present for Chargers database
      const jsonData = JSON.stringify(results);
      const jsonObject = JSON.parse(jsonData);

      let obj = jsonObject[0];
      let errors = [];

      // Check for required fields (excluding id and user_id as they're auto-generated)
      const requiredFields = phasesDataList.filter(
        (field) => field !== "id" && field !== "user_id" && field !== "ports",
      );

      requiredFields.forEach((key) => {
        // Check if the key exists in the object, accounting for potential whitespace/control chars
        const keyExists = Object.keys(obj).some(
          (objKey) => objKey.trim() === key.trim() || objKey.replace(/\r$/, "") === key,
        );

        if (!keyExists) {
          errors.push(`\n missing key: ${key}`);
        }
      });

      if (errors.length > 0) {
        throw new Error(`Chargers data are missing required fields:${errors}`);
      } else {
        const fleetDataResponse = await fetchData(userId, "fleetData");
        const uniqueDomiciles = [
          ...new Set(fleetDataResponse.fleetData.map((item) => item["Simplified Domicile"])),
        ];

        results = results.filter((item) => uniqueDomiciles.includes(item.site));

        if (results.length > 0) {
          for (let i = 0; i < results.length; i++) {
            const phaseResponse = await addPhase(results[i]);

            if (phaseResponse.error) {
              throw new Error(`Failed to add Chargers data: ${phaseResponse.error.message}`);
            }
          }
        } else {
          setMessage("There is no charger data to update because there is no site name matching");
        }
      }
    } catch (error) {
      setMessage(`Error parsing Charger CSV: ${error.message}`);
      return { error };
    }
  };

  /**
   * @function uploadCSVFile - Uploads the CSV file to the server and creates the default controls data
   * Note***** If the controls data fails to upload, the user is deleted
   */
  const uploadCSVFile = async (userId, domicileAddressMap) => {
    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("userId", userId);
    if (domicileAddressMap) {
      formData.append("domicileAddressMap", JSON.stringify(domicileAddressMap));
    }

    try {
      const defaultControlsData = {
        id: userId,
        electrification_scenario: "",
        site: "",
        incentives: true,
        ira_incentives: true,
        evse_incentives: true,
      };
      const defaultAdvancedControls = {
        id: userId,
        inflation: false,
        inflation_escalation_rate: 3,
        electricity_escalation_rate: 3,
        gasoline_escalation_rate: 3,
        infrastructure_loan_term: 10,
        infrastructure_loan_interest_rate: 3,
        discount_rate_npv: 3,
        maintenance_costs_annual_per_station: 400,
        charging_optimization: false,
        charging_optimization_savings: 1000,
        charge_management_subscription_costs: 0,
        charger_network_costs: 0,
      };

      // Call the /api/controls endpoint with the default data
      const controlsResponse = await createControls(defaultControlsData);
      const advancedControlRes = await addAdvancedControl(defaultAdvancedControls);
      const chargerCostAddResponse = await addChargerCost(userId);
      const userMetadata = await createUserMetadata(userId);

      if (
        controlsResponse.error ||
        advancedControlRes.error ||
        chargerCostAddResponse.error ||
        userMetadata.error
      ) {
        throw new Error("Failed to set controls data");
      }

      const response = await uploadWithOutDelete(formData);

      if (response.error) {
        throw new Error(`Vehicle CSV format is incorrect.\n: ${response.error}`);
      }
    } catch (error) {
      setMessage(`Error parsing Vehicle CSV: ${error.message}`);
      return { error };
    }
  };

  return (
    <div className="w-full h-full flex justify-center items-center">
      <Card className="h-full w-[500px] p-4">
        <CardHeader className="pb-5">
          <CardTitle>Pro Forma Account Addition</CardTitle>
          <CardDescription>Add Pro Forma Users Here</CardDescription>
          <p className="whitespace-pre-wrap text-sm text-red-400">{message}</p>
          <p className="text-sm text-[#008000]">{notificationMessage}</p>
          {notificationMessage && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate("/")}>
              Proceed now
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup}>
            <div className="grid w-full items-center gap-4">
              <div className="flex flex-col space-y-1.5">
                <Label>New Username</Label>
                <Input
                  type="text"
                  placeholder="username"
                  value={email}
                  onChange={(e) => {
                    if (e.target.value.length >= MAX_LENGTH) {
                      setMessage(`Username must be less than ${MAX_LENGTH} characters`);
                    } else {
                      setMessage("");
                    }
                    setEmail(e.target.value);
                  }}
                  maxlength={MAX_LENGTH}
                  required
                />
              </div>
              <div className="flex flex-col space-y-1.5">
                <Label>New Password</Label>
                <Input
                  type="text"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    if (!validatePassword(e.target.value)) {
                      setMessage(
                        "Password must be at least 8 characters long, include at least one capital letter (A-Z), include at least one number, and at least one special character.",
                      );
                    } else {
                      setMessage("");
                    }
                    setPassword(e.target.value);
                  }}
                  required
                />
              </div>

              <div
                className="border border-4 rounded-md border-dashed p-5 hover:border-gray-400 text-center cursor-pointer"
                {...getCSVRootProps()}>
                <Input {...getCSVInputProps()} />
                {csvFile ? <p>{csvFile.name}</p> : <p>Drag & Drop Vehicle CSV file here</p>}
              </div>

              <div
                className="border border-4 rounded-md border-dashed p-5 hover:border-gray-400 text-center cursor-pointer"
                {...getChargerRootProps()}>
                <Input {...getChargerInputProps()} />
                {chargerFile ? (
                  <p>{chargerFile.name}</p>
                ) : (
                  <p>
                    Drag & Drop Charger CSV file here
                    <br />
                    (Optional)
                  </p>
                )}
              </div>

              <div className="flex flex-col space-y-1.5">
                <Button
                  type="submit"
                  disabled={
                    message !== "" ||
                    csvFile === null ||
                    isCreatingAccount ||
                    isCheckingUser
                  }>
                  {isCheckingUser ? "Checking username…" : "Create Account"}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <AddressConfirmationDialog
        open={addressDialogOpen}
        groups={domicileGroups}
        confirming={isCreatingAccount}
        onCancel={() => {
          if (!isCreatingAccount) setAddressDialogOpen(false);
        }}
        onConfirm={createAccountWithAddresses}
      />
    </div>
  );
};

export default Admin;
